/**
 * Object-style API client matching the original src/lib/utils/api.ts surface
 * (api.get/post/patch/put/delete), but Bearer-authenticated and pointed at the
 * Hono Worker via PUBLIC_API_BASE_URL. Ported pages/hooks use it unchanged.
 *
 * Callers pass full paths beginning with `/api/...` (as before).
 */
import { env } from '@/env.generated';
import { authReady, getAccessToken, refreshAccessToken } from '@/lib/supabase';

const BASE = env.API_BASE_URL;

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
	body?: unknown;
	params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(url: string, options: FetchOptions = {}): Promise<T> {
	const { body, params, headers: customHeaders, ...rest } = options;

	let fullUrl = `${BASE}${url}`;
	if (params) {
		const searchParams = new URLSearchParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) searchParams.set(key, String(value));
		}
		const qs = searchParams.toString();
		if (qs) fullUrl += `?${qs}`;
	}

	const baseHeaders: Record<string, string> = { ...(customHeaders as Record<string, string>) };
	if (body !== undefined) baseHeaders['Content-Type'] = 'application/json';
	const serializedBody = body !== undefined ? JSON.stringify(body) : undefined;

	const send = (token: string | null) =>
		fetch(fullUrl, {
			...rest,
			headers: token ? { ...baseHeaders, Authorization: `Bearer ${token}` } : baseHeaders,
			body: serializedBody,
		});

	// ADR-006: read the token synchronously from the in-memory mirror (awaiting
	// only the one-time startup seed). On a 401 the cached token may have just
	// expired — refresh once from supabase-js and retry a single time.
	await authReady;
	let res = await send(getAccessToken());
	if (res.status === 401) {
		res = await send(await refreshAccessToken());
	}

	if (!res.ok) {
		const text = await res.text().catch(() => 'Unknown error');
		let message = text;
		try {
			const json = JSON.parse(text);
			if (json.details && json.error) message = `${json.error}: ${json.details}`;
			else if (json.details) message = json.details;
			else if (json.error) message = json.error;
			else if (json.message) message = json.message;
		} catch {
			/* not JSON */
		}
		console.error(`[API ERROR ${res.status}] ${url}:`, message);
		throw new ApiError(res.status, message);
	}

	if (res.status === 204) return undefined as T;
	return res.json() as Promise<T>;
}

/**
 * Drop-in replacement for `fetch()` when the URL is an `/api/...` path: prepends
 * PUBLIC_API_BASE_URL and injects the Bearer token. Returns the raw Response
 * (used by streaming/feed calls and bare fetches in ported hooks/pages).
 */
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
	await authReady;
	const headers = new Headers(init.headers);
	const token = getAccessToken();
	if (token) headers.set('Authorization', `Bearer ${token}`);
	if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type'))
		headers.set('Content-Type', 'application/json');
	const url = input.startsWith('/api') ? `${BASE}${input}` : input;
	const res = await fetch(url, { ...init, headers });
	// Retry once on a stale-token 401, but only when the body is safe to resend
	// (absent or a plain string — never a consumed stream).
	const bodyReusable = init.body == null || typeof init.body === 'string';
	if (res.status === 401 && bodyReusable) {
		const fresh = await refreshAccessToken();
		if (fresh) {
			headers.set('Authorization', `Bearer ${fresh}`);
			return fetch(url, { ...init, headers });
		}
	}
	return res;
}

export const api = {
	get<T>(url: string, params?: Record<string, string | number | boolean | undefined>) {
		return request<T>(url, { method: 'GET', params });
	},
	post<T>(url: string, body?: unknown) {
		return request<T>(url, { method: 'POST', body });
	},
	patch<T>(url: string, body?: unknown) {
		return request<T>(url, { method: 'PATCH', body });
	},
	put<T>(url: string, body?: unknown) {
		return request<T>(url, { method: 'PUT', body });
	},
	delete<T>(url: string, body?: unknown) {
		return request<T>(url, { method: 'DELETE', body });
	},
};
